import moment from "moment";
import SimpleBar from "simplebar-react";
import React from "react";
import { Col, Row } from "react-bootstrap";
import { FiSettings, FiCheckCircle } from "react-icons/fi";
import {
  HorizontalLine,
  LoadMoreButton,
  MarkReadButton,
  NotificationMessageContainer,
} from "./NotificationStyle";
import { COLOR } from "../common/uiTokens";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchNotificationFeed,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationsRead,
} from "../../features/notificationSlice";
import { parseNotificationDate, targetOf } from "../../helper/notifications";

function NotificationMessage({ onNavigate }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // The bell is an overlay the header keeps mounted, so it has to be told to
  // close whenever a click here takes the user somewhere.
  const goTo = (target) => {
    if (onNavigate) onNavigate();
    navigate(target);
  };
  const {
    rows = [],
    unreadCount = 0,
    page = 1,
    totalPage = 0,
    loadStatus,
    writeFailed,
  } = useSelector((state) => state.root.notification);

  const handleNotificationClick = (notification) => {
    if (!notification.readAtUtc) {
      dispatch(markNotificationsRead({ ids: [notification.id] }));
    }

    // Marking read must never block the navigation the user actually asked for.
    const target = targetOf(notification);
    if (target) goTo(target);
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  // A failed page still leaves state.page on the last one that landed, so the
  // retry is the same call.
  const handleLoadMore = () => {
    dispatch(fetchNotificationFeed({ page: page + 1 }));
  };

  // Same pair the bell fetches on mount, so a retry restores the full state.
  const handleRetry = () => {
    dispatch(fetchNotificationFeed());
    dispatch(fetchUnreadCount());
  };

  const placeholderStyle = {
    minHeight: "120px",
    padding: "12px 16px 20px",
    color: COLOR.textMuted,
    fontSize: "13px",
    fontWeight: 500,
    gap: "10px",
  };

  const renderRow = (notification) => {
    const createdAt = parseNotificationDate(notification.createdAtUtc);
    const isUnread = !notification.readAtUtc;
    // Some rows are legitimately unclickable — a deleted maintenance job, the
    // budget events, anything with no entity at all. An entity type this build
    // has no route for is still clickable: it lands on a "not here yet" page.
    const isClickable = Boolean(targetOf(notification));

    return (
      <Col
        md={12}
        className="d-flex flex-row justify-content-center"
        key={notification.id}
      >
        <NotificationMessageContainer
          className={`mt-2 ${isUnread ? "unread" : ""} ${
            isClickable ? "clickable" : ""
          }`}
          role={isClickable ? "button" : "presentation"}
          tabIndex={isClickable ? 0 : undefined}
          onClick={
            isClickable ? () => handleNotificationClick(notification) : undefined
          }
          onKeyDown={
            isClickable
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleNotificationClick(notification);
                  }
                }
              : undefined
          }
        >
          <div className="MessageHeader mb-1">
            {isUnread && <span className="unread-dot" />}
            {notification.title}
          </div>
          {notification.body && (
            <div className="MessageBody mb-1">{notification.body}</div>
          )}
          <div className="d-flex flex-row justify-content-between">
            <span className="datetime">
              {createdAt ? moment(createdAt).format("dddd h:mm a") : ""}
            </span>
            <span className="datetime">
              {createdAt ? moment(createdAt).format("DD/MM/YYYY") : ""}
            </span>
          </div>
        </NotificationMessageContainer>
      </Col>
    );
  };

  return (
    <div>
      <Row className="p-3 align-items-center">
        <Col></Col>
        <Col
          className="text-center"
          style={{ color: COLOR.textPrimary, fontSize: 16, fontWeight: 700 }}
        >
          Notifications
        </Col>
        <Col className="text-end">
          <FiSettings
            role="button"
            tabIndex={0}
            title="Notification settings"
            size={16}
            color={COLOR.textSecondary}
            style={{ cursor: "pointer" }}
            onClick={() => goTo("/profile?tab=notification")}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                goTo("/profile?tab=notification");
              }
            }}
          />
        </Col>
      </Row>
      {/* NOTE: Tab selection is intentionally hidden until additional notification views such as Mentions are implemented. */}
      <>
        <SimpleBar style={{ maxHeight: "310px", overflowX: "hidden" }}>
          {rows.length > 0 ? (
            <Row className="pb-2 px-2">
              {rows.map(renderRow)}
              {/* A failed page leaves the rows already loaded on screen, so the
                  failure has to be said here — the empty-state retry never runs. */}
              {(page < totalPage || loadStatus === "error") && (
                <Col
                  md={12}
                  className="d-flex flex-column align-items-center mt-2"
                >
                  {loadStatus === "error" && (
                    <div
                      style={{
                        color: COLOR.danger,
                        fontSize: "11.5px",
                        fontWeight: 500,
                      }}
                      role="alert"
                    >
                      Couldn&apos;t load more notifications.
                    </div>
                  )}
                  <LoadMoreButton
                    onClick={handleLoadMore}
                    disabled={loadStatus === "loading"}
                  >
                    {loadStatus === "loading"
                      ? "Loading..."
                      : loadStatus === "error"
                        ? "Try again"
                        : "Load more"}
                  </LoadMoreButton>
                </Col>
              )}
            </Row>
          ) : loadStatus === "error" ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-center"
              style={placeholderStyle}
            >
              <div>Notifications couldn&apos;t be loaded.</div>
              <MarkReadButton onClick={handleRetry}>Try again</MarkReadButton>
            </div>
          ) : loadStatus === "loading" || loadStatus === "idle" ? (
            <div
              className="d-flex align-items-center justify-content-center text-center"
              style={placeholderStyle}
            >
              Loading notifications...
            </div>
          ) : (
            <div
              className="d-flex align-items-center justify-content-center text-center"
              style={placeholderStyle}
            >
              No new notifications
            </div>
          )}
        </SimpleBar>
        <div className="p-0">
          <HorizontalLine />
          {/* A failed write leaves rows looking unread; say so rather than let
              the user click into the same silence again. */}
          {writeFailed && (
            <div
              className="px-2 pt-2"
              style={{ color: COLOR.danger, fontSize: "11.5px", fontWeight: 500 }}
              role="alert"
            >
              Couldn&apos;t update. Please try again.
            </div>
          )}
          <div className="p-2">
            <MarkReadButton
              className="w-100"
              variant="primary"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
            >
              <FiCheckCircle size={13} />
              Mark all as read
            </MarkReadButton>
          </div>
        </div>
      </>
    </div>
  );
}

export default NotificationMessage;
